import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { FileText, Upload, Download, Trash2, AlertCircle, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DocumentsManager = () => {
  const [documents, setDocuments] = useState([]);
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadForm, setUploadForm] = useState({
    file: null,
    user_group: 'pack_modeles',
    description: ''
  });

  const groupLabels = {
    pack_modeles: 'Pack Modèles Propriétaire',
    audit_express: 'Audit Express',
    accompagnement_litige: 'Accompagnement Litige'
  };

  const groupColors = {
    pack_modeles: 'bg-blue-100 text-blue-800',
    audit_express: 'bg-green-100 text-green-800',
    accompagnement_litige: 'bg-purple-100 text-purple-800'
  };

  useEffect(() => {
    fetchDocuments();
    fetchStats();
  }, []);

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/client/admin/documents/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDocuments(response.data);
    } catch (error) {
      console.error('Error fetching documents:', error);
      toast.error('Erreur lors du chargement des documents');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/client/admin/groups/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.type === 'application/pdf') {
      setUploadForm({ ...uploadForm, file });
    } else {
      toast.error('Veuillez sélectionner un fichier PDF');
      e.target.value = null;
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    
    if (!uploadForm.file) {
      toast.error('Veuillez sélectionner un fichier');
      return;
    }

    try {
      setUploading(true);
      const token = localStorage.getItem('admin_token');
      const adminEmail = localStorage.getItem('admin_email');
      
      const formData = new FormData();
      formData.append('file', uploadForm.file);
      formData.append('user_group', uploadForm.user_group);
      formData.append('description', uploadForm.description);
      formData.append('admin_email', adminEmail);

      await axios.post(`${API}/client/admin/documents/upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success('Document uploadé avec succès');
      setUploadForm({ file: null, user_group: 'pack_modeles', description: '' });
      document.getElementById('file-input').value = null;
      fetchDocuments();
      fetchStats();
    } catch (error) {
      console.error('Error uploading document:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'upload');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (documentId, filename) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer "${filename}" ?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(`${API}/client/admin/documents/${documentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Document supprimé avec succès');
      fetchDocuments();
      fetchStats();
    } catch (error) {
      console.error('Error deleting document:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-6">
      {/* Header with Stats */}
      <div>
        <h1 className="text-3xl font-bold text-slate-900 mb-2">Gestion des Documents</h1>
        <p className="text-slate-600">Gérez les fichiers PDF accessibles par les clients</p>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {stats.map((stat) => (
          <Card key={stat.group_name}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600 mb-1">
                    {stat.display_name.fr}
                  </p>
                  <p className="text-3xl font-bold text-slate-900">
                    {stat.document_count}
                  </p>
                  <p className="text-sm text-slate-500 mt-1">
                    {stat.user_count} client{stat.user_count > 1 ? 's' : ''}
                  </p>
                </div>
                <div className={`p-4 rounded-full ${groupColors[stat.group_name]}`}>
                  <FileText className="h-6 w-6" />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Upload Form */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5" />
            Uploader un nouveau document
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleUpload} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="file-input">Fichier PDF *</Label>
                <Input
                  id="file-input"
                  type="file"
                  accept=".pdf"
                  onChange={handleFileChange}
                  className="mt-2"
                  required
                />
                {uploadForm.file && (
                  <p className="text-sm text-green-600 mt-2 flex items-center gap-1">
                    <CheckCircle className="h-4 w-4" />
                    {uploadForm.file.name} ({formatFileSize(uploadForm.file.size)})
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="user_group">Groupe d'utilisateurs *</Label>
                <select
                  id="user_group"
                  value={uploadForm.user_group}
                  onChange={(e) => setUploadForm({ ...uploadForm, user_group: e.target.value })}
                  className="w-full mt-2 px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  {Object.entries(groupLabels).map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <Label htmlFor="description">Description (optionnelle)</Label>
              <Input
                id="description"
                type="text"
                value={uploadForm.description}
                onChange={(e) => setUploadForm({ ...uploadForm, description: e.target.value })}
                placeholder="Ex: Modèle de bail vierge"
                className="mt-2"
              />
            </div>

            <Button
              type="submit"
              disabled={uploading || !uploadForm.file}
              className="bg-blue-900 hover:bg-blue-800"
            >
              {uploading ? 'Upload en cours...' : 'Uploader le document'}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Documents List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Documents ({documents.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">
              <p className="text-slate-500">Chargement...</p>
            </div>
          ) : documents.length === 0 ? (
            <div className="text-center py-8">
              <AlertCircle className="h-12 w-12 text-slate-400 mx-auto mb-4" />
              <p className="text-slate-500">Aucun document pour le moment</p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Group by user_group */}
              {Object.entries(groupLabels).map(([groupKey, groupLabel]) => {
                const groupDocs = documents.filter(doc => doc.user_group === groupKey);
                if (groupDocs.length === 0) return null;

                return (
                  <div key={groupKey} className="border rounded-lg p-4">
                    <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                      <Badge className={groupColors[groupKey]}>
                        {groupLabel} ({groupDocs.length})
                      </Badge>
                    </h3>
                    <div className="space-y-2">
                      {groupDocs.map((doc) => (
                        <div
                          key={doc.id}
                          className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                        >
                          <div className="flex items-center gap-3 flex-1">
                            <FileText className="h-5 w-5 text-slate-600" />
                            <div className="flex-1">
                              <p className="font-medium text-slate-900">
                                {doc.original_filename}
                              </p>
                              {doc.description && (
                                <p className="text-sm text-slate-500">{doc.description}</p>
                              )}
                              <p className="text-xs text-slate-400 mt-1">
                                {formatFileSize(doc.file_size)} • Uploadé le {formatDate(doc.uploaded_at)}
                              </p>
                            </div>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDelete(doc.id, doc.original_filename)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default DocumentsManager;
